import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  ArrowLeft,
  CheckCircle2,
  Pause,
  Pencil,
  Plus,
  Server as ServerIcon,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '../../../lib/api-client.js';
import { Button } from '../../../shared/components/ui/Button.js';
import { Card } from '../../../shared/components/ui/Card.js';
import { serversApi, type Tenant } from '../api.js';
import { CreateTenantModal } from '../components/CreateTenantModal.js';
import { EditTenantModal } from '../components/EditTenantModal.js';
import { TenantUsersSheet } from '../components/TenantUsersSheet.js';

export function ServerDetail() {
  const { id } = useParams({ from: '/authLayout/servidores/$id' });
  const serverId = Number(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [usersSheetTenant, setUsersSheetTenant] = useState<Tenant | null>(null);
  const [editTenant, setEditTenant] = useState<Tenant | null>(null);

  const serversQuery = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
  });
  const server = serversQuery.data?.find((s) => s.id === serverId);

  const healthQuery = useQuery({
    queryKey: ['server-health', serverId],
    queryFn: () => serversApi.health(serverId),
    retry: false,
    enabled: !!server,
  });

  const tenantsQuery = useQuery({
    queryKey: ['tenants', serverId],
    queryFn: () => serversApi.tenants(serverId),
    enabled: !!server,
  });

  const toggleMut = useMutation({
    mutationFn: async ({ tenant, newStatus }: { tenant: Tenant; newStatus: 'active' | 'suspended' }) =>
      serversApi.patchTenant(serverId, tenant.id, { status: newStatus }),
    onSuccess: (data, vars) => {
      void qc.invalidateQueries({ queryKey: ['tenants', serverId] });
      if (vars.newStatus === 'suspended') {
        toast.success(`Tenant "${vars.tenant.name}" suspenso`, {
          description: `${data.revoked_user_count ?? 0} usuário(s) foram desconectados.`,
        });
      } else {
        toast.success(`Tenant "${vars.tenant.name}" reativado`);
      }
    },
    onError: (err) => {
      if (err instanceof ApiError) toast.error(err.problem.title, { description: err.problem.detail });
    },
  });

  if (serversQuery.isLoading) {
    return <div className="py-12 text-center text-[13px] text-n-500">Carregando...</div>;
  }

  if (!server) {
    return (
      <Card className="p-12 text-center">
        <h3 className="text-[16px] font-semibold text-n-700">Servidor não encontrado</h3>
        <Button className="mt-4" variant="outline" onClick={() => navigate({ to: '/' })}>
          ← Voltar
        </Button>
      </Card>
    );
  }

  const tenants = tenantsQuery.data ?? [];
  const isUp = healthQuery.data?.status === 200;

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate({ to: '/' })}
          className="mb-3 inline-flex items-center gap-1.5 text-[12px] text-n-500 hover:text-n-700"
        >
          <ArrowLeft size={14} /> Servidores
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-[48px] w-[48px] items-center justify-center rounded-lg bg-brand-50 text-brand-600">
              <ServerIcon size={24} strokeWidth={1.75} />
            </div>
            <div>
              <h1 className="text-[24px] font-bold text-n-900">{server.name}</h1>
              <div className="flex items-center gap-3 text-[12px] text-n-500">
                <span className="font-mono">{server.url}</span>
                <span
                  className={
                    isUp
                      ? 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700'
                      : 'rounded-pill bg-danger-50 px-2 py-0.5 text-[10px] font-semibold text-danger-700'
                  }
                >
                  {isUp ? 'ONLINE' : 'OFFLINE'}
                </span>
              </div>
            </div>
          </div>
          <Button onClick={() => setCreateOpen(true)} disabled={!isUp}>
            <Plus size={14} /> Criar Tenant
          </Button>
        </div>
      </div>

      {/* Counters */}
      {healthQuery.data?.counters && (
        <div className="mb-6 grid grid-cols-3 gap-4">
          <Card className="p-4">
            <div className="text-[11px] font-medium uppercase text-n-500">Tenants</div>
            <div className="mt-1 text-[28px] font-bold text-n-900">
              {healthQuery.data.counters.tenants.total}
            </div>
            <div className="text-[11px] text-n-500">
              {healthQuery.data.counters.tenants.by_status.active ?? 0} ativos ·{' '}
              {healthQuery.data.counters.tenants.by_status.suspended ?? 0} suspensos
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-[11px] font-medium uppercase text-n-500">Usuários ativos</div>
            <div className="mt-1 text-[28px] font-bold text-n-900">
              {healthQuery.data.counters.users_active}
            </div>
            <div className="text-[11px] text-n-500">total no servidor</div>
          </Card>
          <Card className="p-4">
            <div className="text-[11px] font-medium uppercase text-n-500">Mensagens (30d)</div>
            <div className="mt-1 text-[28px] font-bold text-n-900">
              {healthQuery.data.counters.messages_last_30d.toLocaleString('pt-BR')}
            </div>
            <div className="text-[11px] text-n-500">últimos 30 dias</div>
          </Card>
        </div>
      )}

      {/* Tabela de tenants */}
      <Card>
        <table className="w-full">
          <thead className="border-b border-n-200 bg-n-50 text-left text-[11px] font-semibold uppercase text-n-500">
            <tr>
              <th className="px-5 py-3">ID</th>
              <th className="px-5 py-3">Nome</th>
              <th className="px-5 py-3">Slug</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3">Usuários</th>
              <th className="px-5 py-3">Criado em</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody>
            {tenantsQuery.isLoading && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-[13px] text-n-500">
                  Carregando tenants...
                </td>
              </tr>
            )}
            {tenants.length === 0 && !tenantsQuery.isLoading && (
              <tr>
                <td colSpan={7} className="px-5 py-8 text-center text-[13px] text-n-500">
                  Nenhum tenant ainda. Clique em "Criar Tenant" pra adicionar o primeiro.
                </td>
              </tr>
            )}
            {tenants.map((t) => {
              const suspended = t.status === 'suspended';
              return (
                <tr key={t.id} className="border-b border-n-100 hover:bg-n-50">
                  <td className="px-5 py-3 text-[12px] font-mono text-n-500">{t.id}</td>
                  <td className="px-5 py-3 text-[13px] font-medium text-n-900">{t.name}</td>
                  <td className="px-5 py-3 text-[12px] font-mono text-n-600">{t.slug}</td>
                  <td className="px-5 py-3">
                    <span
                      className={
                        suspended
                          ? 'rounded-pill bg-danger-50 px-2 py-0.5 text-[10px] font-semibold text-danger-700'
                          : 'rounded-pill bg-success-50 px-2 py-0.5 text-[10px] font-semibold text-success-700'
                      }
                    >
                      {suspended ? 'SUSPENSO' : 'ATIVO'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-[12px]">
                    <button
                      type="button"
                      onClick={() => setUsersSheetTenant(t)}
                      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-brand-600 hover:bg-brand-50 hover:text-brand-700"
                      title="Ver usuários do tenant"
                    >
                      <Users size={12} />
                      <span>
                        {t.users.active}/{t.users.total}
                      </span>
                    </button>
                  </td>
                  <td className="px-5 py-3 text-[12px] text-n-500">
                    {new Date(t.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setEditTenant(t)}>
                        <Pencil size={12} /> Editar
                      </Button>
                      <Button
                        variant={suspended ? 'primary' : 'outline'}
                        size="sm"
                        onClick={() =>
                          toggleMut.mutate({
                            tenant: t,
                            newStatus: suspended ? 'active' : 'suspended',
                          })
                        }
                        loading={
                          toggleMut.isPending && toggleMut.variables?.tenant.id === t.id
                        }
                      >
                        {suspended ? (
                          <>
                            <CheckCircle2 size={12} /> Reativar
                          </>
                        ) : (
                          <>
                            <Pause size={12} /> Suspender
                          </>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <CreateTenantModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        serverId={serverId}
      />

      {usersSheetTenant && (
        <TenantUsersSheet
          open={!!usersSheetTenant}
          onClose={() => setUsersSheetTenant(null)}
          serverId={serverId}
          tenant={usersSheetTenant}
        />
      )}

      {editTenant && (
        <EditTenantModal
          open={!!editTenant}
          onClose={() => setEditTenant(null)}
          serverId={serverId}
          tenant={editTenant}
        />
      )}
    </div>
  );
}
